import { useEffect, useRef } from 'react';
import { API_BASE } from '../config/api';
import AuthService from '../services/authService';

/**
 * Debounced trip autosave, extracted from useTripState.
 *
 * Persists itinerary changes after 2s of inactivity — each PUT rewrites the
 * full itinerary server-side, so the window collapses an editing burst (drag
 * session, rapid adds) into one save. A flush on real unmount/beforeunload
 * fires any pending payload immediately, so "edit then navigate" can't lose
 * data.
 *
 * Contract with the caller (useTripState):
 * - `tripDays`/`transports`/`savedItineraryRef` drive the O(1) dirty check:
 *   the itinerary is unsaved iff the current arrays are not the ones in the
 *   last loaded/saved snapshot. The effect re-arms whenever those arrays or
 *   `buildPayload` (identity tracks all payload inputs) change.
 * - `buildPayload` is called only at fire time — building eagerly per
 *   keystroke re-allocated the whole days×activities tree while the debounce
 *   was still being re-armed.
 * - `onSaved(tripId, effectiveEndDate, result)` runs the caller's post-save
 *   bookkeeping (cache busts + trip re-baseline, and the server's answer:
 *   new stop ids, rows it kept that this planner didn't send).
 * - `onFailed({ status, code, error })` (optional) hears a save the server
 *   REFUSED, so the caller can tell the user — e.g. `code: 'transport_limit'`
 *   when the trip is over a transport cap. The itinerary stays unsaved.
 */
function useTripAutosave({ tripId, enabled, canSave = true, isSaving, hasName, tripDays, transports, savedItineraryRef, buildPayload, onSaved, onFailed }) {
  // Holds { tripId, build } while a save is pending; nulled once persisted.
  const latestAutosavePayloadRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  // The trip open now, for answers that arrive after the page moved on.
  const openTripIdRef = useRef(tripId);
  openTripIdRef.current = tripId;

  useEffect(() => {
    // No edit rights (a view-only traveller, or one just made so): nothing
    // pending may go out — not now, not as the unmount flush — since the
    // server would only refuse it. Unlike `enabled` (off while a trip
    // loads), this drops the pending payload.
    if (!canSave) {
      latestAutosavePayloadRef.current = null;
      return;
    }
    if (!enabled) return;
    // Skip if the initial load hasn't completed yet
    if (savedItineraryRef.current === null) {
      latestAutosavePayloadRef.current = null;
      return;
    }
    // Skip if nothing changed (O(1) identity compare — this effect also runs
    // on every metadata keystroke via its deps)
    if (tripDays === savedItineraryRef.current.tripDays
      && transports === savedItineraryRef.current.transports) {
      latestAutosavePayloadRef.current = null;
      return;
    }
    // Skip if a manual save is in progress
    if (isSaving) return;
    // Skip until a name is set (matches the timer's own guard)
    if (!hasName) {
      latestAutosavePayloadRef.current = null;
      return;
    }

    latestAutosavePayloadRef.current = { tripId, build: buildPayload };
    // Itinerary content this payload represents (buildPayload closes over the
    // same arrays). Edits made while the save is in flight swap in new
    // arrays, so they still read as unsaved after.
    const payloadItinerary = { tripDays, transports };

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(async () => {
      autoSaveTimeoutRef.current = null;
      // Guard against race conditions
      if (isAutoSavingRef.current) return;

      isAutoSavingRef.current = true;
      try {
        const response = await fetch(`${API_BASE}/api/trips/${tripId}`, {
          method: 'PUT',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(buildPayload())
        });

        if (response.ok) {
          // The page may have moved to another trip while this was in flight
          // (the planner stays mounted across /trip/:id): this answer's
          // itinerary is not the open trip's baseline.
          if (openTripIdRef.current !== tripId) {
            latestAutosavePayloadRef.current = null;
            return;
          }
          savedItineraryRef.current = payloadItinerary;
          latestAutosavePayloadRef.current = null;
          // The answer carries the ids new stops got and what the server
          // kept that this planner didn't send (see plannerSaveDiff).
          const result = await response.json().catch(() => null);
          onSaved(tripId, undefined, result);
        } else {
          const errBody = await response.json().catch(() => ({}));
          console.error('Auto-save failed:', response.status, errBody.error || errBody);
          if (onFailed) onFailed({ status: response.status, code: errBody.code || null, error: errBody.error || null });
        }
      } catch (err) {
        console.error('Auto-save failed:', err);
      } finally {
        isAutoSavingRef.current = false;
      }
    }, 2000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [enabled, canSave, tripId, isSaving, hasName, tripDays, transports, buildPayload, onSaved, onFailed, savedItineraryRef]);

  // Flush any pending autosave on real unmount or page-unload. Without this,
  // a 2-second debounce window means edits + immediate navigation = silent
  // data loss. The cleanup below runs on the navigation that unmounts the
  // hook (e.g. clicking a Link to a detail page), and beforeunload covers
  // tab close / hard refresh.
  useEffect(() => {
    const flushPending = () => {
      const pending = latestAutosavePayloadRef.current;
      if (!pending) return false;
      // Cancel the still-pending debounce timer; we're firing now.
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      // Fire-and-forget. The browser keeps the connection open even after
      // this React tree unmounts, and the server-side write is what we
      // care about — the response won't be read by anything.
      try {
        fetch(`${API_BASE}/api/trips/${pending.tripId}`, {
          method: 'PUT',
          headers: AuthService.getJsonHeaders(),
          body: JSON.stringify(pending.build()),
          // keepalive lets the request survive document teardown on
          // browsers that support it (most modern ones).
          keepalive: true
        }).catch(() => { /* swallow — we're tearing down */ });
      } catch (_) { /* swallow */ }
      latestAutosavePayloadRef.current = null;
      return true;
    };

    const handleBeforeUnload = () => {
      // sendBeacon is the only call guaranteed to land during unload, but
      // it can't set custom headers (Authorization). Fall through to the
      // keepalive-fetch path which DOES carry headers — works for in-app
      // navigation. For true tab-close with Authorization headers there
      // is no perfect solution; the best we can do is try.
      flushPending();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushPending();
    };
    // Empty deps: this effect should only set up on mount and tear down on
    // real unmount. flushPending reads via refs so it always sees the
    // latest payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useTripAutosave;
